import { goalbookUrls, toolkitUrls } from '@/util/links.js'
import { ContentListResponse, LibraryFolder, LibraryFolders, LibraryFolderResponse, ContributorUserInfo, ContributorRoleChangeResponse, DeleteResourceResponse, LibraryStrategy, LibraryGoal, FolderUsersResponse, CollectionNewContributionsResponse, UserNewContributionsResponse, FolderComment, FolderCommentsResponse } from '@/library/library_types'
import { getHandleRequest, getCSRFHeaders } from '@/authentication/request_handler'
import { sendHTTPRequest } from "@/util/http.ts";
import type { SavedStrategyDetail } from '@/strategies/strategy_types'
import type { SavedAnchorUpdatePayload } from '@/goals/anchor_page_types'

export async function getFolderResources(uuid: string, params = {}): Promise<LibraryFolderResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: getFolderResourcesRequest }, uuid, params) as Promise<LibraryFolderResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getFolderResourcesRequest(folderUuid: string, params = {}): Promise<Response> {
  const urlParams = new URLSearchParams(params)
  const url = `${toolkitUrls.apiBases.collections}/${folderUuid}?${urlParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getSharedFolder(shareToken: string, params = {}): Promise<LibraryFolderResponse> {
  const urlParams = new URLSearchParams(params)
  const url = `${toolkitUrls.apiBases.collections}/share/${shareToken}?${urlParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  const responseJSON = await response.json()
  if (response.status >= 200 && response.status < 300) {
    return responseJSON
  }
  throw new Error('Failed to get shared folder', { cause: responseJSON })
}

export async function deleteFolderResource(folderUuid: string, contentUuid: string, resourceType: string): Promise<DeleteResourceResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: deleteFolderResourceRequest }, folderUuid, contentUuid, resourceType) as Promise<DeleteResourceResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function deleteFolderResourceRequest(folderUuid: string, contentUuid: string, resourceType: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/${folderUuid}/${contentUuid}/${resourceType}`
  return await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}


export async function getRecentFolders(userUuid: string): Promise<LibraryFolder[]> {
  return getHandleRequest()({ requestFn: getRecentFoldersRequest }, userUuid) as Promise<LibraryFolder[]>
}

export async function getRecentFoldersRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/${userUuid}/recent_students`
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}

export async function getFolders(userId: string): Promise<LibraryFolders> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getFoldersRequest }, userId) as Promise<LibraryFolders>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getFoldersRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/customresources/${userUuid}/students`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getArchivedFolders(userId: string): Promise<LibraryFolders> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getArchivedFoldersRequest }, userId) as Promise<LibraryFolders>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getArchivedFoldersRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/customresources/${userUuid}/students/archived`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function addFolder(folderName: string, userUuid: string): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: addFolderRequest }, folderName, userUuid) as Promise<LibraryFolder>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addFolderRequest(folderName: string, userUuid: string): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student`
    const response = await fetch(url, {
      method: 'POST',
      headers: getCSRFHeaders(),
      body: JSON.stringify({
        name: folderName
      }),
      credentials: 'include'
    })

    return response
}

export async function updateCollectionContent({ collectionUuid, contentUuid, url, contentTitle, userUuid }: {
  collectionUuid: string, contentUuid: string, url?: string, contentTitle?: string, userUuid: string
}): Promise<unknown | null> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: updateCollectionContentRequest }, { collectionUuid, contentUuid, url, contentTitle, userUuid }) as Promise<unknown | null>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function updateCollectionContentRequest({ collectionUuid, contentUuid, url, contentTitle, userUuid }: {
  collectionUuid: string, contentUuid: string, url?: string, contentTitle?: string, userUuid: string
}): Promise<Response> {
  const endpointUrl = `${goalbookUrls.toolkit}/api/library/attachment/${contentUuid}`
  const payload: Record<string, unknown> = {
    collection_uuid: collectionUuid,
    user_uuid: userUuid
  }
  if (url !== undefined) {
    payload.url = url
  }
  if (contentTitle !== undefined) {
    payload.content_title = contentTitle
  }
  const response = await fetch(endpointUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })
  return response
}

export async function updateFolder({ userUuid, folderUuid, updateParams }: {
  userUuid: string, folderUuid: string, updateParams: {[key: string]: unknown}
}): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: updateFolderRequest }, userUuid, folderUuid, updateParams) as Promise<LibraryFolder>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function updateFolderRequest(userUuid: string, folderUuid: string, updateParams: {[key: string]: unknown}): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student/${folderUuid}`
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify(updateParams),
    })
    return response
}

export async function deleteFolderRequest(userUuid: string, folderUuid: string): Promise<Response> {
    const url = `${toolkitUrls.apiBases.library}/${userUuid}/student/${folderUuid}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getCSRFHeaders(),
      credentials: 'include'
    })
    return response
}

export async function deleteFolder({ userUuid, folderUuid }: { userUuid: string, folderUuid: string }): Promise<unknown | null> {
   const handleRequest = getHandleRequest();
    if (handleRequest) {
      return handleRequest({ requestFn: deleteFolderRequest }, userUuid, folderUuid) as Promise<unknown>
    }
    return Promise.reject(new Error('Request handler not initialized'))
  }

export async function getAllResourcesForContentTypes(userUuid: string, contentTypes: string[], include_shared: boolean = false): Promise<ContentListResponse> {
  const contentTypesString = encodeURIComponent(contentTypes.join(','))
  const queryParams = new URLSearchParams({ include_shared: `${include_shared}` })
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.apiBases.library}/${userUuid}/allresources/${contentTypesString}?${queryParams.toString()}`,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as ContentListResponse
  } catch (e) {
    throw new Error('Failed to get all resources for content types', { cause: e })
  }
}

export async function getRecentlyViewedContent(userUuid: string, contentType: string, limit?: number): Promise<ContentListResponse> {
  let url = `${toolkitUrls.apiBases.library}/${userUuid}/recently_viewed/${contentType}`
  if (limit) {
    url += `?limit=${limit}`
  }
  try {
    const response = await sendHTTPRequest({
      url: url,
      method: 'GET',
      includeCredentials: true
    })
    return await response.json() as ContentListResponse
  } catch (e) {
    throw new Error('Failed to get recent content', { cause: e })
  }
}

export async function addCollectionContent({ collectionUuid, contentType, contentUuid, contributor, url, contentTitle }: {
  collectionUuid: string, contentType: string, contentUuid: string, contributor: string, url?: string, contentTitle?: string
}): Promise<unknown | null> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addCollectionContentRequest}, { collectionUuid, contentType, contentUuid, contributor, url, contentTitle }) as Promise<unknown | null>
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function addCollectionContentRequest({ collectionUuid, contentType, contentUuid, contributor, url, contentTitle }: {
  collectionUuid: string, contentType: string, contentUuid: string, contributor: string, url?: string, contentTitle?: string
}): Promise<Response> {
    const baseUrl = 'api/collections'
    const endpointUrl = `${goalbookUrls.toolkit}/${baseUrl}/content/add`
    const payload: Record<string, unknown> = {
      collection_uuid: collectionUuid,
      content_type: contentType,
      content_uuid: contentUuid,
      contributor: contributor
    }
    if (url !== undefined) {
      payload.url = url
    }
    if (contentTitle !== undefined) {
      payload.content_title = contentTitle
    }
    const response = await fetch(endpointUrl, {
      method: 'POST',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify(payload)
    })
    return response
}

export async function copyCollection(folderUuid: string, nameOfCopy: string): Promise<LibraryFolder> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: copyCollectionRequest }, folderUuid, nameOfCopy) as Promise<LibraryFolder>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function copyCollectionRequest(folderUuid: string, nameOfCopy: string): Promise<Response> {
  const baseUrl = 'api/collections'
  const url = `${goalbookUrls.toolkit}/${baseUrl}/${folderUuid}/copy`
  const response = await fetch(url, {
    method: 'POST',
    headers: getCSRFHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      name_of_copy: nameOfCopy
    })
  })
  return response
}


async function addGoalToFolderRequest(folder: { uuid: string, name: string }, goalPayload: Record<string, any>): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/goal`
  const payload = {
    student_uuid: folder.uuid,
    student_name: folder.name,
    for_student: true,
    ...goalPayload
  }
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })

  return response
}

export async function addGoalToFolder(folder: { uuid: string, name: string }, goalPayload: Record<string, any>): Promise<LibraryGoal> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addGoalToFolderRequest}, folder, goalPayload) as Promise<LibraryGoal>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addStrategyToFolder({
  userUuid,
  strategyUuid,
  folderUuid,
  folderName,
  notes,
}: {
  userUuid: string,
  strategyUuid: string,
  folderUuid: string,
  folderName: string,
  notes?: string,
}): Promise<LibraryStrategy> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({requestFn: addStrategyToFolderRequest}, { userUuid, strategyUuid, folderUuid, folderName, notes }) as Promise<LibraryStrategy>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function addStrategyToFolderRequest({
  userUuid,
  strategyUuid,
  folderUuid,
  folderName,
  notes,
}: {
  userUuid: string,
  strategyUuid: string,
  folderUuid: string,
  folderName: string,
  notes?: string,
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/strategy`
  const payload = {
    user_uuid: userUuid,
    strategy_uuid: strategyUuid,
    student_uuid: folderUuid,
    student_name: folderName,
    notes: notes
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })

  return response
}

export async function getCollectionUsers(collectionUuid: string): Promise<FolderUsersResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getCollectionUsersRequest }, collectionUuid) as Promise<FolderUsersResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getCollectionUsersRequest(collectionUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/collection/${collectionUuid}/users`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function queryUsersInDomain(domainUuuid: string, queryString: string): Promise<ContributorUserInfo[]> {
  try {
    const response = await sendHTTPRequest(
      {
        url: `${goalbookUrls.pathwaysApi}/domain/${domainUuuid}/users?query=${encodeURIComponent(queryString)}`,
        method: 'GET',
        includeCredentials: true
      })
    return await response.json()
  } catch (e) {
    throw new Error('Failed to users in domain', { cause: e })
  }
}
export async function grantContributors(collectionUuid: string, targetUsers: string[], emailMessage?: string): Promise<ContributorRoleChangeResponse | null> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: grantContributorsRequest }, { collectionUuid, targetUsers, emailMessage }) as Promise<ContributorRoleChangeResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function grantContributorsRequest({ collectionUuid, targetUsers, emailMessage = '' }: {
  collectionUuid: string, targetUsers: string[], emailMessage?: string
}): Promise<Response> {
  const body: { target_users: string[], send_email?: boolean, email_message?: string } = {
    target_users: targetUsers,
    send_email: true
  }
  if (emailMessage && emailMessage.length > 0) {
    body.email_message = emailMessage
  }
  const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/contributors`, {
    method: 'POST',
    headers: getCSRFHeaders(),
    body: JSON.stringify(body),
    credentials: 'include'
  })
  return response
}

export async function revokeContributors(collectionUuid: string, targetUsers: string[]): Promise<ContributorRoleChangeResponse | null> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: revokeContributorsRequest }, { collectionUuid, targetUsers }) as Promise<ContributorRoleChangeResponse>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function revokeContributorsRequest({ collectionUuid, targetUsers }: {
  collectionUuid: string, targetUsers: string[]
}): Promise<Response> {
    const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/contributors/revoke`, {
      method: 'POST',
      headers: getCSRFHeaders(),
      body: JSON.stringify({
        target_users: targetUsers,
      }),
      credentials: 'include'
    })
    return response
}

export async function transferOwnership(collectionUuid: string, newOwner: string): Promise<{ message: string }> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: transferOwnershipRequest }, { collectionUuid, newOwner }) as Promise<{ message: string }>;
  }
  return Promise.reject(new Error('Request handler not initialized'));
}

export async function transferOwnershipRequest({ collectionUuid, newOwner }: { collectionUuid: string, newOwner: string }): Promise<Response> {
  const response = await fetch(`${toolkitUrls.api}/collection/${collectionUuid}/transfer`, {
    method: 'POST',
    headers: getCSRFHeaders(),
    body: JSON.stringify({
      new_owner: newOwner,
    }),
    credentials: 'include'
  })
  return response
}

export async function getCollectionNewContributions(collectionUuid: string): Promise<CollectionNewContributionsResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getCollectionNewContributionsRequest }, collectionUuid) as Promise<CollectionNewContributionsResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getCollectionNewContributionsRequest(collectionUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/collection/${collectionUuid}/new_contributions`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getSavedCopiesOfStrategy(userUuid: string, strategyUuid: string): Promise<LibraryStrategy[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.apiBases.library}/${userUuid}/source_strategy/${strategyUuid}`
    })
    return await response.json() as LibraryStrategy[]
  } catch (e) {
    throw new Error('Failed to get saved copies of strategy', { cause: e })
  }
}

export async function getSavedCopyCountOfGoal(userUuid: string, goalUuid: string): Promise<number> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.apiBases.library}/${userUuid}/count/${goalUuid}`
    })
    return await response.json() as number
  } catch (e) {
    throw new Error('Failed to get saved copy count of goal', { cause: e })
  }
}

export async function getSavedStrategy(savedStrategyUuid: string): Promise<SavedStrategyDetail> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: getSavedStrategyRequest }, savedStrategyUuid) as Promise<SavedStrategyDetail>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getSavedStrategyRequest(savedStrategyUuid: string): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/strategy/${savedStrategyUuid}`, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}

export async function putSavedStrategy(savedStrategy: { uuid: string, notes: string }): Promise<SavedStrategyDetail> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: putSavedStrategyRequest }, savedStrategy) as Promise<SavedStrategyDetail>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function putSavedStrategyRequest(savedStrategy: { uuid: string, notes: string }): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/strategy/${savedStrategy.uuid}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(savedStrategy)
  })
}

export async function shareSavedStrategy(savedStrategyUuid: string): Promise<{ share_link: string }> {
  return setSavedStrategySharing(savedStrategyUuid, 'share') as Promise<{ share_link: string }>
}

export async function unshareSavedStrategy(savedStrategyUuid: string): Promise<unknown> {
  return setSavedStrategySharing(savedStrategyUuid, 'unshare')
}

interface LibraryResponseBody {
  status?: string
  message?: string
}

// The library reports a missing or unshareable strategy as HTTP 200 with `status: "error"`.
async function setSavedStrategySharing(savedStrategyUuid: string, action: 'share' | 'unshare'): Promise<unknown> {
  const handleRequest = getHandleRequest()
  if (!handleRequest) {
    throw new Error('Request handler not initialized')
  }
  const response = await handleRequest({ requestFn: setSavedStrategySharingRequest }, savedStrategyUuid, action) as LibraryResponseBody
  if (response.status === 'error') {
    throw new Error(`Failed to ${action} saved strategy: ${response.message}`)
  }
  return response
}

export async function setSavedStrategySharingRequest(savedStrategyUuid: string, action: 'share' | 'unshare'): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/strategy/${savedStrategyUuid}/${action}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}

export async function shareSavedGoal(savedGoalUuid: string): Promise<{ share_link: string }> {
  return setSavedGoalSharing(savedGoalUuid) as Promise<{ share_link: string }>
}

export async function unshareSavedGoal(savedGoalUuid: string): Promise<unknown> {
  return setSavedGoalSharing(savedGoalUuid, { unshare: true })
}

// Sharing sends no body, as Vue 2 did; only unsharing says so. Like the strategy route, the
// library can report a failure as HTTP 200 with `status: "error"`.
async function setSavedGoalSharing(savedGoalUuid: string, body?: { unshare: boolean }): Promise<unknown> {
  const handleRequest = getHandleRequest()
  if (!handleRequest) {
    throw new Error('Request handler not initialized')
  }
  const response = await handleRequest({ requestFn: setSavedGoalSharingRequest }, savedGoalUuid, body) as LibraryResponseBody
  if (response.status === 'error') {
    throw new Error(`Failed to ${body ? 'unshare' : 'share'} saved goal: ${response.message}`)
  }
  return response
}

export async function setSavedGoalSharingRequest(savedGoalUuid: string, body?: { unshare: boolean }): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/share/goal/${savedGoalUuid}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: body && JSON.stringify(body)
  })
}

/** A refused read rejects with the request handler's `{ status_code }`, so a caller can tell a 403 from any other failure. */
export async function getLibraryGoal(libraryGoalUuid: string): Promise<LibraryGoal> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: getLibraryGoalRequest }, libraryGoalUuid) as Promise<LibraryGoal>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getLibraryGoalRequest(libraryGoalUuid: string): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/goal/${libraryGoalUuid}`, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
}

// Credentialed, as in Vue 2: a viewer who reached the copy through a shared folder is let in by its share-token cookie.
export async function getSharedLibraryGoal(libraryGoalUuid: string): Promise<LibraryGoal> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.apiBases.library}/shared/goal/${libraryGoalUuid}`,
      urlParams: { author: 'true' }
    })
    return await response.json() as LibraryGoal
  } catch (e) {
    throw new Error('Failed to get shared library goal', { cause: e })
  }
}

// The library reports some refused updates as HTTP 200 with `status: "error"`, as it does for strategy sharing.
export async function putLibraryGoal(libraryGoalUuid: string, payload: SavedAnchorUpdatePayload): Promise<LibraryGoal> {
  const handleRequest = getHandleRequest()
  const response = await handleRequest({ requestFn: putLibraryGoalRequest }, libraryGoalUuid, payload) as LibraryGoal | LibraryResponseBody
  if ('status' in response && response.status === 'error') {
    throw new Error(`Failed to save library goal: ${response.message}`)
  }
  return response as LibraryGoal
}

export async function putLibraryGoalRequest(libraryGoalUuid: string, payload: SavedAnchorUpdatePayload): Promise<Response> {
  return fetch(`${toolkitUrls.apiBases.library}/goal/${libraryGoalUuid}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify(payload)
  })
}

export async function getUserNewContributionCounts(userUuid: string): Promise<UserNewContributionsResponse> {
  const handleRequest = getHandleRequest();
  if (handleRequest) {
    return handleRequest({ requestFn: getUserNewContributionCountsRequest }, userUuid) as Promise<UserNewContributionsResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function getUserNewContributionCountsRequest(userUuid: string): Promise<Response> {
  const url = `${toolkitUrls.api}/library/${userUuid}/new_contribution_counts`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export type AttachmentUploadUrlResponse = {
  url: string
  key: string
  expires_at: string
  // Metadata fields required for S3 upload (must be sent as x-amz-meta-* headers)
  folder_uuid: string
  file_uuid: string
  user_uuid: string
  title: string
  file_name: string
  file_extension: string
}

export async function generateAttachmentUploadUrl({
  folderUuid,
  fileUuid,
  contentType,
  fileTitle,
  fileName,
  userUuid,
}: {
  folderUuid: string
  fileUuid: string
  contentType: string
  fileTitle: string
  fileName: string
  userUuid: string
}): Promise<AttachmentUploadUrlResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: generateAttachmentUploadUrlRequest }, { folderUuid, fileUuid, contentType, fileTitle, fileName, userUuid }) as Promise<AttachmentUploadUrlResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function generateAttachmentUploadUrlRequest({
  folderUuid,
  fileUuid,
  contentType,
  fileTitle,
  fileName,
  userUuid,
}: {
  folderUuid: string
  fileUuid: string
  contentType: string
  fileTitle: string
  fileName: string
  userUuid: string
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/collection/${folderUuid}/files/upload-url`
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({ file_uuid: fileUuid, content_type: contentType, title: fileTitle, file_name: fileName, user_uuid: userUuid }),
  })
  return response
}

export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  metadata: Pick<AttachmentUploadUrlResponse, 'folder_uuid' | 'file_uuid' | 'user_uuid' | 'title' | 'file_name' | 'file_extension'>,
): Promise<void> {
  const headers: Record<string, string> = {
    'x-amz-meta-folder-uuid': metadata.folder_uuid,
    'x-amz-meta-content-uuid': metadata.file_uuid,
    'x-amz-meta-uploaded-by': metadata.user_uuid,
  }
  if (metadata.title) headers['x-amz-meta-file-title'] = metadata.title
  if (metadata.file_name) headers['x-amz-meta-file-name'] = metadata.file_name
  if (metadata.file_extension) headers['x-amz-meta-file-extension'] = metadata.file_extension

  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers,
    body: file,
  })
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(`Failed to upload file to S3 (${response.status} ${response.statusText}${body ? `: ${body}` : ''})`)
  }
}

export async function saveFileAttachment({
  collectionUuid,
  contentUuid,
  fileTitle,
  fileType,
  fileExtension,
  contributor,
}: {
  collectionUuid: string
  contentUuid: string
  fileTitle: string
  fileType: string
  fileExtension: string
  contributor: string
}): Promise<unknown> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: saveFileAttachmentRequest }, { collectionUuid, contentUuid, fileTitle, fileType, fileExtension, contributor })
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function saveFileAttachmentRequest({
  collectionUuid,
  contentUuid,
  fileTitle,
  fileType,
  fileExtension,
  contributor,
}: {
  collectionUuid: string
  contentUuid: string
  fileTitle: string
  fileType: string
  fileExtension: string
  contributor: string
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.collections}/content/add`
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({
      collection_uuid: collectionUuid,
      content_type: 'attachment_file',
      content_uuid: contentUuid,
      contributor,
      content_title: fileTitle,
      file_type: fileType,
      file_extension: fileExtension,
    }),
  })
  return response
}

export async function generateAttachmentFileUpdateUrl({
  folderUuid,
  contentUuid,
  contentType,
  fileTitle,
  fileName,
  userUuid,
}: {
  folderUuid: string
  contentUuid: string
  contentType: string
  fileTitle: string
  fileName: string
  userUuid: string
}): Promise<AttachmentUploadUrlResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: generateAttachmentFileUpdateUrlRequest }, { folderUuid, contentUuid, contentType, fileTitle, fileName, userUuid }) as Promise<AttachmentUploadUrlResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function generateAttachmentFileUpdateUrlRequest({
  folderUuid,
  contentUuid,
  contentType,
  fileTitle,
  fileName,
  userUuid,
}: {
  folderUuid: string
  contentUuid: string
  contentType: string
  fileTitle: string
  fileName: string
  userUuid: string
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/collection/${folderUuid}/files/${contentUuid}`
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({ content_type: contentType, title: fileTitle, file_name: fileName, user_uuid: userUuid }),
  })
  return response
}

export async function updateFileAttachment({
  collectionUuid,
  contentUuid,
  fileTitle,
  fileName,
  fileType,
  fileExtension,
  userUuid,
}: {
  collectionUuid: string
  contentUuid: string
  fileTitle: string
  fileName: string
  fileType: string
  fileExtension: string
  userUuid: string
}): Promise<unknown> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: updateFileAttachmentRequest }, { collectionUuid, contentUuid, fileTitle, fileName, fileType, fileExtension, userUuid })
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function updateFileAttachmentRequest({
  collectionUuid,
  contentUuid,
  fileTitle,
  fileName,
  fileType,
  fileExtension,
  userUuid,
}: {
  collectionUuid: string
  contentUuid: string
  fileTitle: string
  fileName: string
  fileType: string
  fileExtension: string
  userUuid: string
}): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/attachment/${contentUuid}`
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({
      collection_uuid: collectionUuid,
      user_uuid: userUuid,
      url: '',
      content_title: fileTitle,
      file_name: fileName,
      file_type: fileType,
      file_extension: fileExtension,
    }),
  })
  return response
}

export type AttachmentDownloadUrlResponse = {
  url: string
  expires_at: string
}

export async function generateAttachmentDownloadUrl(contentUuid: string, folderUuid: string): Promise<AttachmentDownloadUrlResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: generateAttachmentDownloadUrlRequest }, contentUuid, folderUuid) as Promise<AttachmentDownloadUrlResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function generateAttachmentDownloadUrlRequest(contentUuid: string, folderUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/collection/${encodeURIComponent(folderUuid)}/files/${encodeURIComponent(contentUuid)}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders(),
  })
  return response
}

export async function fetchFolderComments(folderUuid: string): Promise<FolderCommentsResponse> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: fetchFolderCommentsRequest }, folderUuid) as Promise<FolderCommentsResponse>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function fetchFolderCommentsRequest(folderUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/${folderUuid}/comments`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function createComment(folderUuid: string, content: string): Promise<FolderComment> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: createCommentRequest }, folderUuid, content) as Promise<FolderComment>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function createCommentRequest(folderUuid: string, content: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/${folderUuid}/comments`
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({ content })
  })
  return response
}

export async function deleteComment(folderUuid: string, commentUuid: string): Promise<void> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: deleteCommentRequest }, folderUuid, commentUuid) as Promise<void>
  }
  return Promise.reject(new Error('Request handler not initialized'))
}

export async function deleteCommentRequest(folderUuid: string, commentUuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/${folderUuid}/comments/${commentUuid}`
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}